import React from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import styled from "styled-components";
import {
  FinancialByCategoryResponse,
  FinancialCashFlowResponse
} from "../../common/interfaces";

const brl = (n: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(n || 0);

const cssVar = (name: string, fallback: string) => {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v || fallback;
};

const PALETTE = [
  "#2b7bff",
  "#22d3ee",
  "#34d399",
  "#fbbf24",
  "#fb7185",
  "#a78bfa",
  "#f472b6",
  "#60a5fa",
  "#facc15",
  "#4ade80"
];

const Wrapper = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: var(--shadow);
  padding: 20px 22px;
`;

const Head = styled.div`
  margin-bottom: 14px;
`;

const Eyebrow = styled.span`
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-dim);
`;

const HeadTitle = styled.h2`
  margin: 4px 0 0;
  font-family: var(--font-display);
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text);
`;

const Placeholder = styled.div`
  height: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-dim);
  font-size: 0.9rem;
`;

export const FinancialCashFlowChart: React.FC<{
  data: FinancialCashFlowResponse | null;
  loading?: boolean;
  height?: number;
}> = ({ data, loading, height = 280 }) => {
  const grid = cssVar("--border", "#e6e8f4");
  const textMuted = cssVar("--text-muted", "#5b6079");
  const series = data?.series ?? [];

  return (
    <Wrapper>
      <Head>
        <Eyebrow>Fluxo de caixa</Eyebrow>
        <HeadTitle>Entradas × Saídas × Saldo</HeadTitle>
      </Head>
      <div style={{ width: "100%", height }}>
        {loading ? (
          <Placeholder>Carregando…</Placeholder>
        ) : series.length === 0 ? (
          <Placeholder>Sem lançamentos no período.</Placeholder>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series} margin={{ left: 4, right: 4 }}>
              <CartesianGrid stroke={grid} vertical={false} />
              <XAxis
                dataKey="period"
                tick={{ fill: textMuted, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: textMuted, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) =>
                  v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`
                }
              />
              <Tooltip
                formatter={(value: number) => brl(value)}
                contentStyle={{
                  background: cssVar("--surface", "#fff"),
                  border: `1px solid ${grid}`,
                  borderRadius: 10
                }}
              />
              <Legend />
              <Bar dataKey="income" name="Entradas" fill="#34d399" radius={[6, 6, 0, 0]} />
              <Bar dataKey="expense" name="Saídas" fill="#fb7185" radius={[6, 6, 0, 0]} />
              <Line
                type="monotone"
                dataKey="balance"
                name="Saldo"
                stroke="#2b7bff"
                strokeWidth={2.5}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </Wrapper>
  );
};

export const FinancialByCategoryChart: React.FC<{
  data: FinancialByCategoryResponse | null;
  loading?: boolean;
  height?: number;
}> = ({ data, loading, height = 280 }) => {
  const items = data?.items ?? [];
  const title = data?.type === "INCOME" ? "Entradas por categoria" : "Despesas por categoria";

  return (
    <Wrapper>
      <Head>
        <Eyebrow>Distribuição</Eyebrow>
        <HeadTitle>{title}</HeadTitle>
      </Head>
      <div style={{ width: "100%", height }}>
        {loading ? (
          <Placeholder>Carregando…</Placeholder>
        ) : items.length === 0 ? (
          <Placeholder>Sem lançamentos no período.</Placeholder>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={items}
                dataKey="value"
                nameKey="category_name"
                innerRadius="55%"
                outerRadius="80%"
                paddingAngle={2}
              >
                {items.map((item, i) => (
                  <Cell
                    key={item.category_id}
                    fill={item.color || PALETTE[i % PALETTE.length]}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => brl(value)} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </Wrapper>
  );
};
